// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// VIEW — "Since 1992".
//
// WHY THIS IS NOT A BAR CHART. A bar of height 46 erases 46 separate matters
// into a number. Every determination here is its own 10x10 square, packed three
// across into its year's column, so 2012 reads as forty-six court decisions
// about forty-six particular areas rather than as a tall rectangle.
//
// WHY IT DIVERGES. The 130 determinations that found native title does not
// exist are 19.0% of the register. Stacked in with everything else they are a
// footnote; packed downward from a centre baseline they are structural — you
// cannot look at the chart without seeing them. They are drawn at full weight
// in the slate hue the whole site uses for that finding. They are NOT coloured
// as a failure, they are not faded, and they are not red, because "native title
// does not exist" is a court's finding about a piece of ground and not a
// verdict on the people who brought the application.
//
// TWO CHANNELS, KEPT APART. Fill carries WHAT the court found. Method — consent,
// unopposed, litigated — is a STROKE on the same square and never a hue, so the
// two can be read independently and cannot be cross-tabulated by colour into a
// "litigation succeeds more often than negotiation" framing this site does not
// make and the data does not support.

import { FINDING, FINDING_ORDER, METHOD, METHOD_ORDER, UNRECORDED, type Finding, type Method } from '../theme';
import { el, chart, measure } from '../components/svg';
import { esc, num, km2, pct, longDate, yearOf, plural, tip, tipAttr } from '../format';
import { openDetermination } from '../components/detail';
import { glossTerm, glossIcon } from '../components/glossary';
import { navigate, setParams, currentRoute } from '../router';
import { attachSvgZoom } from '../utils/svgZoom';
import { getMeta, getDeterminations, getDetermination, loadSeries } from '../data';
import type { Determination } from '../types';

/** The ink every method ring is drawn in. Method is a stroke, never a hue. */
const RING_INK = '#1a1815';

/** Geometry. One square per determination, three across, packed from the baseline. */
const CELL = 10;
const GAP = 2;
const PER_ROW = 3;
const BLOCK = PER_ROW * CELL + (PER_ROW - 1) * GAP;
const COL_GAP = 8;
const MIN_PITCH = BLOCK + COL_GAP;
const PAD_L = 46;
const PAD_R = 16;
const PAD_T = 30;
const PAD_B = 42;
const BASE_GAP = 5;

/** Everything except "does not exist" packs upward, in the register's fixed order. */
const ABOVE_ORDER: Finding[] = FINDING_ORDER.filter((f) => f !== 'none');

/** The jurisdictions in the usual administrative order — including the two real zeroes. */
const JURIS: { code: string; name: string }[] = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT', name: 'Northern Territory' },
  { code: 'ACT', name: 'Australian Capital Territory' },
];

type Kind = Finding | 'unrecorded';

interface Mark {
  d: Determination;
  g: SVGGElement;
}

function kindOf(d: Determination): Kind {
  return d.finding ?? 'unrecorded';
}

function kindLabel(k: Kind): string {
  return k === 'unrecorded' ? 'Outcome not recorded' : FINDING[k].label;
}

function kindFill(k: Kind): string {
  return k === 'unrecorded' ? UNRECORDED : FINDING[k].fill;
}

/** Compensation is drawn UNFILLED, so a category of four can never be rounded away. */
function isOutlined(k: Kind): boolean {
  return k === 'compensation';
}

export async function renderTimeline(host: HTMLElement): Promise<void> {
  const meta = getMeta();
  const dets = getDeterminations();
  const series = await loadSeries();

  const total = dets.length;
  const byMethodAll = series.byMethod;
  const byFindingAll = series.byFinding;
  const consent = byMethodAll.consent ?? 0;
  const unopposed = byMethodAll.unopposed ?? 0;
  const litigated = byMethodAll.litigated ?? 0;
  const noneCount = byFindingAll.none ?? 0;

  const judgeCount = new Map(series.byJudge.map((j) => [j.judge, j.count]));
  const jurisCount = new Map<string, number>();
  for (const j of JURIS) jurisCount.set(j.code, 0);
  for (const d of dets) jurisCount.set(d.juris, (jurisCount.get(d.juris) ?? 0) + 1);

  const kindCount = new Map<Kind, number>();
  for (const d of dets) {
    const k = kindOf(d);
    kindCount.set(k, (kindCount.get(k) ?? 0) + 1);
  }

  const years = series.perYear.map((p) => p.year);
  const lastYear = years[years.length - 1];
  const latest = meta.latestDetermination;
  const peak = series.perYear.reduce((a, b) => (b.count > a.count ? b : a), series.perYear[0]);

  // ── group the register into its year columns ──────────────────────────────
  const byYear = new Map<number, Determination[]>();
  for (const y of years) byYear.set(y, []);
  let undated = 0;
  for (const d of dets) {
    const y = yearOf(d.date);
    if (y == null || !byYear.has(y)) { undated += 1; continue; }
    byYear.get(y)!.push(d);
  }

  const chron = (a: Determination, b: Determination) =>
    (a.date ?? '').localeCompare(b.date ?? '') || a.id.localeCompare(b.id);

  /** Column stacks: above the baseline in the register's fixed order, below it the "does not exist" findings. */
  const stacks = new Map<number, { up: Determination[]; down: Determination[] }>();
  let maxUp = 0;
  let maxDown = 0;
  for (const y of years) {
    const rows = byYear.get(y)!;
    const up: Determination[] = [];
    for (const f of ABOVE_ORDER) up.push(...rows.filter((r) => r.finding === f).sort(chron));
    up.push(...rows.filter((r) => r.finding == null).sort(chron));
    const down = rows.filter((r) => r.finding === 'none').sort(chron);
    stacks.set(y, { up, down });
    maxUp = Math.max(maxUp, up.length);
    maxDown = Math.max(maxDown, down.length);
  }

  const rowsUp = Math.max(1, Math.ceil(maxUp / PER_ROW));
  const rowsDown = Math.max(1, Math.ceil(maxDown / PER_ROW));
  const upH = BASE_GAP + rowsUp * CELL + (rowsUp - 1) * GAP;
  const downH = BASE_GAP + rowsDown * CELL + (rowsDown - 1) * GAP;
  const baselineY = PAD_T + upH;
  const plotBottom = baselineY + downH;
  const chartH = plotBottom + PAD_B;
  const minW = PAD_L + PAD_R + years.length * MIN_PITCH;
  const chartW = Math.max(measure(host), minW);
  const pitch = (chartW - PAD_L - PAD_R) / years.length;

  // ── the page ──────────────────────────────────────────────────────────────
  const wrap = document.createElement('div');
  wrap.className = 'timeline-stack';

  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>Since 1992</h1>
    <p class="lede">Every determination the Federal Court has made since ${esc(longDate(meta.firstDetermination))}
      is one square below — ${esc(num(total))} of them. Nothing is added up into a bar: the
      ${esc(num(peak.count))} determinations recorded for ${esc(String(peak.year))} are
      ${esc(num(peak.count))} separate matters, each one a court decision about a particular area and a
      particular group of people.</p>
    <p>A square sits <strong>above the line</strong> where the court found native title exists over the whole
      area or over parts of it, or decided that compensation is payable. A square sits
      <strong>below the line</strong> where the court found that ${glossTerm('does-not-exist', 'native title does not exist')}
      in the area it considered — ${esc(num(noneCount))} determinations, ${esc(pct((noneCount / total) * 100))} of the
      register. Below the line is not a defeat and not a failure. It is a finding about ground, made on the
      evidence before the court, and it says nothing about the identity or standing of the people who applied.</p>`;

  const stats = document.createElement('div');
  stats.className = 'stat-row';
  const statTiles: [string, string, string, string][] = [
    [num(total), 'determinations', `${longDate(meta.firstDetermination)} to ${longDate(latest)}`,
      tip('The National Native Title Register', `${plural(total, 'determination')} since ${longDate(meta.firstDetermination)}`, `Most recent: ${longDate(latest)}`)],
    [num(consent), 'reached by consent', `${pct((consent / total) * 100)} of all determinations`,
      tip('Consent determinations', `${plural(consent, 'determination')} of ${num(total)}`, METHOD.consent.plain)],
    [num(noneCount), 'found native title does not exist', `${pct((noneCount / total) * 100)} — drawn below the line`,
      tip('Native title does not exist', `${plural(noneCount, 'determination')} of ${num(total)}`, FINDING.none.plain)],
    [num(litigated), 'argued out at trial', `${pct((litigated / total) * 100)} of all determinations`,
      tip('Litigated determinations', `${plural(litigated, 'determination')} of ${num(total)}`, METHOD.litigated.plain)],
  ];
  stats.innerHTML = statTiles.map(([v, l, n, t]) => `<div class="stat" data-tip="${tipAttr(t)}">
      <div class="stat-value">${esc(v)}</div>
      <div class="stat-label">${esc(l)}</div>
      <div class="stat-note">${esc(n)}</div>
    </div>`).join('');

  // ── the chart card ────────────────────────────────────────────────────────
  const card = document.createElement('div');
  card.className = 'card timeline-stack';
  card.innerHTML = `
    <div class="row">
      <div class="grow">
        <div class="card-title">Every determination, one square each</div>
        <div class="card-sub">Placed by the year of the determination. Fill is what the court found;
          the ring on the square is how the matter was reached.</div>
      </div>
      <button type="button" class="btn btn-sm shrink0" id="tl-to-register">Read these as a table</button>
    </div>

    <div class="timeline-filters">
      <div class="timeline-filter-group">
        <span class="field-label"><span id="tl-method-label">How it was reached</span>${glossIcon('consent')}</span>
        <div class="chips timeline-chips" role="group" aria-labelledby="tl-method-label" id="tl-methods"></div>
      </div>
      <div class="timeline-filter-group">
        <span class="field-label" id="tl-juris-label">Jurisdiction</span>
        <div class="chips timeline-chips timeline-juris" role="group" aria-labelledby="tl-juris-label" id="tl-juris"></div>
      </div>
    </div>

    <p class="small muted" id="tl-count"></p>
    <div class="note warn timeline-empty" id="tl-empty" hidden></div>

    <div class="chart-frame timeline-frame">
      <div class="chart-scroll timeline-scroll" id="tl-scroll"></div>
    </div>

    <p class="xsmall muted">The chart scrolls sideways. Scroll-wheel over it, or the + control, zooms in;
      drag to move around once zoomed; double-click resets. Nothing here animates.</p>

    <div class="note timeline-note-partial"><strong>${esc(String(lastYear))} is a partial year.</strong>
      The register runs to ${esc(longDate(latest))}, so the ${esc(String(lastYear))} column holds only part of a year's
      determinations and must not be read as a decline. Its background is striped for that reason.</div>

    <div class="note"><strong>Tasmania and the Australian Capital Territory have no determinations at all.</strong>
      Not missing data, not a gap in the register — no determination has ever been made in either. Both appear in
      the jurisdiction filter above, at zero, and selecting one dims every square on the chart.</div>
  `;

  // ── the SVG ───────────────────────────────────────────────────────────────
  const svg = chart(chartW, chartH, `Every native title determination from ${years[0]} to ${lastYear}, one square each, packed above a centre line where native title was recognised or compensation decided and below it where the court found native title does not exist`);

  const defsEl = el('defs');
  const partial = el('pattern', {
    id: 'tl-partial', patternUnits: 'userSpaceOnUse', width: 6, height: 6, patternTransform: 'rotate(45)',
  });
  partial.append(el('rect', { width: 6, height: 6, fill: '#faf8f4' }));
  partial.append(el('rect', { width: 2.2, height: 6, fill: 'rgba(26,24,21,.07)' }));
  defsEl.append(partial);
  svg.append(defsEl);

  const gCols = el('g');
  const gGrid = el('g');
  const gMarks = el('g', { class: 'timeline-marks' });
  const gAxis = el('g');
  svg.append(gCols, gGrid, gMarks, gAxis);

  // Year columns: a tinted background per year, each with the whole-year summary
  // on hover, so the empty years of 1993–1996 are hoverable, labelled zeroes
  // rather than blank space.
  for (let i = 0; i < years.length; i += 1) {
    const y = years[i];
    const point = series.perYear[i];
    const x = PAD_L + i * pitch;
    const isPartial = y === lastYear;
    const bg = el('rect', {
      x, y: PAD_T - 6, width: pitch, height: plotBottom - PAD_T + 12,
      fill: isPartial ? 'url(#tl-partial)' : (i % 2 === 0 ? 'rgba(26,24,21,.028)' : 'rgba(0,0,0,0)'),
      'pointer-events': 'all',
    });
    const findLines = FINDING_ORDER
      .filter((f) => (point.byFinding[f] ?? 0) > 0)
      .map((f) => `  ${FINDING[f].register}: ${point.byFinding[f]}`);
    const methLines = METHOD_ORDER
      .filter((m) => (point.byMethod[m] ?? 0) > 0)
      .map((m) => `${METHOD[m].label} ${point.byMethod[m]}`);
    bg.setAttribute('data-tip', tip(
      String(y),
      point.count === 0
        ? 'No determinations were made in this year. A real zero, not missing data.'
        : plural(point.count, 'determination'),
      ...findLines,
      methLines.length ? methLines.join(' · ') : null,
      isPartial ? `Partial year — the register runs to ${longDate(latest)}.` : null,
      'Figures are for the whole year, whatever filter is set.',
    ));
    gCols.append(bg);

    if (point.count === 0) {
      const zero = el('text', {
        x: x + pitch / 2, y: baselineY - 6, 'text-anchor': 'middle',
        class: 'axis-text', 'pointer-events': 'none',
      }, '0');
      gCols.append(zero);
    }
  }

  // Row guides. Each row is exactly three determinations, so the guide at five
  // rows is exactly fifteen — an honest count, not an interpolated axis.
  for (const rows of [5, 10, 15, 20]) {
    if (rows >= rowsUp) continue;
    const yPos = baselineY - BASE_GAP - (rows * CELL + (rows - 1) * GAP) - GAP / 2;
    gGrid.append(el('line', { x1: PAD_L, x2: chartW - PAD_R, y1: yPos, y2: yPos, class: 'grid-line' }));
    gGrid.append(el('text', { x: PAD_L - 8, y: yPos + 4, 'text-anchor': 'end', class: 'axis-text' }, String(rows * PER_ROW)));
  }
  for (const rows of [5, 10]) {
    if (rows >= rowsDown) continue;
    const yPos = baselineY + BASE_GAP + (rows * CELL + (rows - 1) * GAP) + GAP / 2;
    gGrid.append(el('line', { x1: PAD_L, x2: chartW - PAD_R, y1: yPos, y2: yPos, class: 'grid-line' }));
    gGrid.append(el('text', { x: PAD_L - 8, y: yPos + 4, 'text-anchor': 'end', class: 'axis-text' }, String(rows * PER_ROW)));
  }

  // The baseline itself.
  gAxis.append(el('line', { x1: PAD_L, x2: chartW - PAD_R, y1: baselineY, y2: baselineY, class: 'baseline' }));
  gAxis.append(el('text', { x: PAD_L + 4, y: PAD_T - 12, class: 'axis-title' },
    '▲ native title recognised, in whole or in part — or compensation decided'));
  gAxis.append(el('text', { x: PAD_L + 4, y: baselineY + downH + 2, class: 'axis-title' },
    '▼ native title does not exist in the area the court considered'));

  for (let i = 0; i < years.length; i += 1) {
    const y = years[i];
    const t = el('text', {
      x: PAD_L + (i + 0.5) * pitch, y: plotBottom + 20, 'text-anchor': 'middle',
      class: 'axis-text', 'pointer-events': 'none',
      fill: y % 5 === 0 ? 'var(--text-secondary)' : null,
      'font-weight': y % 5 === 0 ? '600' : null,
    }, String(y));
    gAxis.append(t);
  }

  // ── the marks ─────────────────────────────────────────────────────────────
  const marks: Mark[] = [];

  function place(d: Determination, cx: number, index: number, below: boolean): void {
    const r = Math.floor(index / PER_ROW);
    const c = index % PER_ROW;
    const x = cx - BLOCK / 2 + c * (CELL + GAP);
    const yPos = below
      ? baselineY + BASE_GAP + r * (CELL + GAP)
      : baselineY - BASE_GAP - (r + 1) * CELL - r * GAP;

    const k = kindOf(d);
    const outlined = isOutlined(k);
    const g = el('g', { class: 'mark hit timeline-mark', role: 'button', tabindex: '-1' });

    g.append(el('rect', {
      x, y: yPos, width: CELL, height: CELL, rx: 2.2,
      fill: outlined ? 'none' : kindFill(k),
      stroke: outlined ? FINDING.compensation.fill : 'none',
      'stroke-width': outlined ? 1.4 : 0,
    }));

    const m = d.method;
    const ring = m ? METHOD[m] : null;
    if (ring && ring.width > 0) {
      const inset = outlined ? 2.6 : 1.2;
      g.append(el('rect', {
        x: x + inset, y: yPos + inset, width: CELL - inset * 2, height: CELL - inset * 2,
        rx: Math.max(0, 2.2 - inset), fill: 'none',
        stroke: RING_INK, 'stroke-width': ring.width,
        'stroke-dasharray': ring.dash || null,
      }));
    }

    // A transparent hit box so the unfilled compensation squares, and the 2px
    // gutters between squares, are as easy to hit as a filled one.
    g.append(el('rect', {
      x: x - 1, y: yPos - 1, width: CELL + 2, height: CELL + 2,
      fill: 'rgba(0,0,0,0)', 'pointer-events': 'all',
    }));

    const judge = d.judge ? d.judge.replace(/,/g, ', ') : null;
    const jn = d.judge ? judgeCount.get(d.judge) : undefined;
    g.setAttribute('data-tip', tip(
      d.name ?? d.id,
      `${longDate(d.date)} · ${d.juris}`,
      d.findingText ?? kindLabel(k),
      m ? `${METHOD[m].label} determination` : 'How it was reached is not recorded',
      d.body ?? null,
      judge ? `${judge}${jn ? ` — ${plural(jn, 'determination')} on this register` : ''}` : 'Judge not recorded',
      // `exact` rounds to whole square kilometres, which prints "0 km²" for the
      // many town-lot and reserve determinations under a square kilometre. Below
      // 1 km² the two-decimal form is the honest one.
      `Determination area ${km2(d.areaSqKm, { exact: (d.areaSqKm ?? 0) >= 1 })}${d.sea ? ' · includes sea country' : ''}`,
      d.appealed ? 'Appealed' : null,
      'Click to read the full record.',
    ));
    g.setAttribute('aria-label', `${d.name ?? d.id}. ${longDate(d.date)}, ${d.juris}. ${d.findingText ?? kindLabel(k)}. ${m ? METHOD[m].label : 'Method not recorded'}.`);

    gMarks.append(g);
    marks.push({ d, g });
  }

  for (let i = 0; i < years.length; i += 1) {
    const cx = PAD_L + (i + 0.5) * pitch;
    const s = stacks.get(years[i])!;
    s.up.forEach((d, k) => place(d, cx, k, false));
    s.down.forEach((d, k) => place(d, cx, k, true));
  }

  // ── legends ───────────────────────────────────────────────────────────────
  const legendKinds: Kind[] = [...FINDING_ORDER];
  if ((kindCount.get('unrecorded') ?? 0) > 0) legendKinds.push('unrecorded');
  const gloss: Record<string, string> = {
    entire: 'native-title', parts: 'determination-area', none: 'does-not-exist', compensation: 'compensation',
  };

  const outcomeLegend = document.createElement('div');
  outcomeLegend.className = 'stack-sm';
  outcomeLegend.innerHTML = `
    <div class="field-label">What the fill means — the court's finding, in the register's own order</div>
    <div class="legend-cards">
      ${legendKinds.map((k) => {
        const n = kindCount.get(k) ?? 0;
        const outlined = isOutlined(k);
        const swatch = outlined
          ? `background:transparent;border:2px solid ${FINDING.compensation.fill}`
          : `background:${kindFill(k)}`;
        const plain = k === 'unrecorded'
          ? 'The register does not state an outcome for these. Not recorded is its own state — never folded in with "does not exist".'
          : FINDING[k].plain;
        const side = k === 'none' ? 'below the line' : 'above the line';
        const key = gloss[k];
        return `<button type="button" class="legend-card timeline-lc" data-term="${esc(key ?? 'determination')}"
            aria-label="What does ${esc(kindLabel(k))} mean?"
            data-tip="${tipAttr(k === 'unrecorded' ? 'Outcome not recorded' : FINDING[k].register, `${plural(n, 'determination')} — ${side}`, plain)}">
          <span class="legend-swatch" style="${swatch}"></span>
          <span class="lc-body">
            <span class="lc-name">${esc(kindLabel(k))}</span>
            <span class="lc-num"> ${esc(num(n))} · ${esc(side)}</span>
            <span class="lc-plain">${esc(plain)}</span>
          </span>
        </button>`;
      }).join('')}
    </div>
    <p class="xsmall muted">Compensation determinations are drawn as an outlined square with no fill —
      there are only ${esc(num(kindCount.get('compensation') ?? 0))} of them on the whole register, and a
      category of four must not be able to disappear into a fill.</p>`;

  const methodLegend = document.createElement('div');
  methodLegend.className = 'stack-sm';
  methodLegend.innerHTML = `
    <div class="field-label">What the ring means — how the matter was reached. Method is a stroke, never a colour.</div>
    <div class="legend">
      ${METHOD_ORDER.map((m) => `<span class="legend-item"
          data-tip="${tipAttr(`${METHOD[m].label} — ${plural(byMethodAll[m] ?? 0, 'determination')}`, METHOD[m].plain)}">
        <span class="timeline-ring timeline-ring--${m}"></span>
        <span>${esc(METHOD[m].label)} — ${esc(num(byMethodAll[m] ?? 0))}</span>
      </span>`).join('')}
    </div>
    <p class="xsmall muted">Keeping method out of the colour channel is deliberate: it makes it structurally
      impossible to read this chart as a scoreboard of negotiation against argument.</p>`;

  card.append(outcomeLegend, methodLegend);

  // ── the consent strip ─────────────────────────────────────────────────────
  const stripCard = document.createElement('div');
  stripCard.className = 'card timeline-stack';
  const segs: { m: Method; n: number }[] = METHOD_ORDER.map((m) => ({ m, n: byMethodAll[m] ?? 0 }));
  stripCard.innerHTML = `
    <div>
      <div class="card-title">The system runs on agreement, not on argument</div>
      <div class="card-sub">All ${esc(num(total))} determinations since ${esc(longDate(meta.firstDetermination))},
        by how they were reached</div>
    </div>
    <div class="timeline-strip" role="group" aria-label="How determinations were reached — select one to filter the chart above">
      ${segs.map(({ m, n }) => `<button type="button" class="timeline-seg timeline-seg--${m}"
          style="flex: 0 0 ${((n / total) * 100).toFixed(3)}%"
          data-strip="${m}" aria-pressed="false"
          aria-label="${esc(METHOD[m].label)}: ${esc(num(n))} of ${esc(num(total))} determinations, ${esc(pct((n / total) * 100))}. Select to filter the chart above."
          data-tip="${tipAttr(METHOD[m].label, `${plural(n, 'determination')} of ${num(total)} — ${pct((n / total) * 100)}`, METHOD[m].plain, 'Click to filter the chart above.')}">
        <span class="timeline-seg-label">${esc(METHOD[m].label)}</span>
      </button>`).join('')}
    </div>
    <div class="timeline-legend-rows">
      ${segs.map(({ m, n }) => `<div class="timeline-legend-row">
        <span class="timeline-ring timeline-ring--${m}"></span>
        <span class="grow">${esc(METHOD[m].label)}</span>
        <span class="mono small shrink0">${esc(num(n))} · ${esc(pct((n / total) * 100))}</span>
      </div>`).join('')}
    </div>
    <p>This is the least appreciated fact on the register. A ${glossTerm('consent', 'consent determination')}
      is one the Court makes because the parties — the claim group, the State or Territory, pastoralists, mining
      companies, local councils, commercial fishers — negotiated the outcome themselves and asked the Court to
      make orders reflecting their agreement. ${esc(num(consent))} of ${esc(num(total))} determinations,
      ${esc(pct((consent / total) * 100))}, were reached that way. A further ${esc(num(unopposed))} were
      ${glossTerm('unopposed', 'unopposed')} — nobody contested them. Only ${esc(num(litigated))},
      ${esc(pct((litigated / total) * 100))}, were ${glossTerm('litigated', 'litigated')}: argued out at trial
      and decided by a judge. Native title in Australia is overwhelmingly negotiated, and the courtroom is the
      exception rather than the rule.</p>
    <p class="small muted">Across the ${esc(num(total))} determinations, ${esc(num(meta.counts.judges))} judges of
      the Federal Court and the High Court appear on the register. Each square's tooltip names the judge who made
      that determination and how many they appear on.</p>`;

  const scope = document.createElement('div');
  scope.className = 'note legal small';
  scope.innerHTML = `A determination is a court's finding about a specific area at a specific time, and a
    determination is not automatically a recognition. Squares are placed by the determination date the register
    records, which is not the date the application was made — many of these matters ran for a decade or more
    before the date shown here. Nothing on this page is legal advice.`;

  wrap.append(head, stats, card, stripCard, scope);
  if (undated > 0) {
    const u = document.createElement('div');
    u.className = 'note warn';
    u.textContent = `${plural(undated, 'determination')} could not be placed on this chart because the register records no determination date for them. They are not shown above and are not counted in any year.`;
    wrap.append(u);
  }
  host.append(wrap);

  // The SVG has to be inside its frame before attachSvgZoom runs, or the zoom
  // controls are appended to nothing and silently never appear.
  const scroll = card.querySelector<HTMLElement>('#tl-scroll')!;
  scroll.append(svg);
  attachSvgZoom(svg);
  // attachSvgZoom sets `touch-action: none` so its own drag-pan can work. At
  // scale 1 that pan is clamped to nothing, so on a phone it would leave the
  // reader unable to reach 2026 at all — the chart is wider than the screen and
  // the browser's own horizontal scroll was the only way across. Hand the
  // horizontal axis back to the browser; wheel-zoom and mouse drag still work.
  svg.style.touchAction = 'pan-x pinch-zoom';

  // ── filters ───────────────────────────────────────────────────────────────
  const params = currentRoute().params;
  const initialMethod = params.get('m');
  const initialJuris = params.get('j');
  let methodFilter: Method | 'all' =
    METHOD_ORDER.includes(initialMethod as Method) ? (initialMethod as Method) : 'all';
  let jurisFilter: string =
    JURIS.some((j) => j.code === initialJuris) ? initialJuris! : 'all';

  const methodBar = card.querySelector<HTMLElement>('#tl-methods')!;
  methodBar.innerHTML = `
    <button type="button" class="pill" data-m="all" aria-pressed="false"
      data-tip="${tipAttr('All ways of reaching a determination', `${plural(total, 'determination')}`)}">All ways · ${esc(num(total))}</button>
    ${METHOD_ORDER.map((m) => `<button type="button" class="pill" data-m="${m}" aria-pressed="false"
      data-tip="${tipAttr(METHOD[m].label, `${plural(byMethodAll[m] ?? 0, 'determination')} of ${num(total)}`, METHOD[m].plain)}">
      <span class="timeline-ring timeline-ring--${m}"></span>${esc(METHOD[m].label)} · ${esc(num(byMethodAll[m] ?? 0))}</button>`).join('')}`;

  const jurisBar = card.querySelector<HTMLElement>('#tl-juris')!;
  jurisBar.innerHTML = `
    <button type="button" class="pill" data-j="all" aria-pressed="false"
      data-tip="${tipAttr('Every jurisdiction', `${plural(total, 'determination')}`)}">All · ${esc(num(total))}</button>
    ${JURIS.map((j) => {
      const n = jurisCount.get(j.code) ?? 0;
      return `<button type="button" class="pill" data-j="${j.code}" aria-pressed="false"
        data-tip="${tipAttr(j.name, n === 0
          ? 'No determination has ever been made here. A real zero, not missing data.'
          : `${plural(n, 'determination')} of ${num(total)}`)}">${esc(j.code)} · ${esc(num(n))}</button>`;
    }).join('')}`;

  const countEl = card.querySelector<HTMLElement>('#tl-count')!;
  const emptyEl = card.querySelector<HTMLElement>('#tl-empty')!;

  function describe(): string {
    const parts: string[] = [];
    if (methodFilter !== 'all') parts.push(`${METHOD[methodFilter].label.toLowerCase()} determinations`);
    if (jurisFilter !== 'all') parts.push(JURIS.find((j) => j.code === jurisFilter)!.name);
    return parts.join(' in ');
  }

  function update(): void {
    let live = 0;
    for (const mk of marks) {
      const on = (methodFilter === 'all' || mk.d.method === methodFilter)
        && (jurisFilter === 'all' || mk.d.juris === jurisFilter);
      mk.g.classList.toggle('dim', !on);
      if (on) live += 1;
    }

    for (const b of methodBar.querySelectorAll<HTMLButtonElement>('[data-m]')) {
      b.setAttribute('aria-pressed', String(b.dataset.m === methodFilter));
    }
    for (const b of jurisBar.querySelectorAll<HTMLButtonElement>('[data-j]')) {
      b.setAttribute('aria-pressed', String(b.dataset.j === jurisFilter));
    }
    for (const b of stripCard.querySelectorAll<HTMLButtonElement>('[data-strip]')) {
      b.setAttribute('aria-pressed', String(b.dataset.strip === methodFilter));
    }

    const what = describe();
    countEl.textContent = methodFilter === 'all' && jurisFilter === 'all'
      ? `All ${num(total)} determinations are drawn. Filtering dims the rest rather than removing it, so the shape of the whole stays visible.`
      : `${num(live)} of ${num(total)} determinations highlighted — ${what}. The remaining ${num(total - live)} are dimmed, not removed.`;

    if (live === 0) {
      emptyEl.hidden = false;
      const j = jurisFilter === 'all' ? null : JURIS.find((x) => x.code === jurisFilter)!;
      emptyEl.innerHTML = j && (jurisCount.get(j.code) ?? 0) === 0
        ? `<strong>${esc(j.name)} has no determinations at all.</strong> Not a gap in the register and not
           missing data — no native title determination has ever been made in ${esc(j.name)}. Every square on the
           chart is dimmed because there is nothing here to highlight.`
        : `<strong>No determination on the register matches this combination</strong> — ${esc(what)}. Every square
           is dimmed. That is a real zero for this combination, not missing data.`;
    } else {
      emptyEl.hidden = true;
      emptyEl.innerHTML = '';
    }
  }

  methodBar.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('[data-m]');
    if (!b) return;
    const v = b.dataset.m!;
    methodFilter = v === 'all' || v === methodFilter ? 'all' : (v as Method);
    setParams({ m: methodFilter === 'all' ? null : methodFilter });
    update();
  });

  jurisBar.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('[data-j]');
    if (!b) return;
    const v = b.dataset.j!;
    jurisFilter = v === 'all' || v === jurisFilter ? 'all' : v;
    setParams({ j: jurisFilter === 'all' ? null : jurisFilter });
    update();
  });

  stripCard.addEventListener('click', (e) => {
    const b = (e.target as Element).closest<HTMLButtonElement>('[data-strip]');
    if (!b) return;
    const v = b.dataset.strip as Method;
    methodFilter = methodFilter === v ? 'all' : v;
    setParams({ m: methodFilter === 'all' ? null : methodFilter });
    update();
    card.scrollIntoView({ block: 'nearest' });
  });

  card.querySelector<HTMLButtonElement>('#tl-to-register')!
    .addEventListener('click', () => navigate('register'));

  // ── keyboard: one tab stop, arrow keys walk the 683 squares ───────────────
  // 683 individual tab stops would make the rest of the page unreachable in
  // practice, so the marks share a single roving tabindex.
  let roving = 0;
  function setRoving(i: number): void {
    if (!marks.length) return;
    marks[roving]?.g.setAttribute('tabindex', '-1');
    roving = Math.max(0, Math.min(marks.length - 1, i));
    marks[roving].g.setAttribute('tabindex', '0');
  }
  if (marks.length) marks[0].g.setAttribute('tabindex', '0');

  marks.forEach((mk, i) => {
    mk.g.addEventListener('click', () => { setRoving(i); openDetermination(mk.d); });
    mk.g.addEventListener('focus', () => { if (roving !== i) setRoving(i); });
  });

  svg.addEventListener('keydown', (e) => {
    const active = document.activeElement;
    const idx = marks.findIndex((mk) => mk.g === active);
    if (idx < 0) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      openDetermination(marks[idx].d);
      return;
    }
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = idx + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = idx - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = marks.length - 1;
    else return;
    e.preventDefault();
    setRoving(next);
    marks[roving].g.focus();
  });

  update();

  // A shared link like #/timeline?id=WPD2026/001 opens straight onto the record.
  const deep = params.get('id');
  if (deep) {
    const d = getDetermination(deep);
    if (d) openDetermination(d);
  }
}
